<template>
    <a-tooltip
        placement="right"
        :overlayClassName="tooltipOverlayClass"
        :mouseEnterDelay="0.7"
    >
        <template slot="title">
            <div v-if="useRichTooltip" class="sft-card">
                <div class="sft-card-title-row">
                    <span v-if="titleMaterialIconName" class="sft-card-title-icon material-icons" aria-hidden="true">{{
                        titleMaterialIconName
                    }}</span>
                    <div class="sft-card-title">{{ titleText }}</div>
                </div>
                <div class="sft-card-body" v-html="richDescriptionHtml"></div>
            </div>
            <span v-else class="sft-simple-title-wrap">
                <span v-if="titleMaterialIconName" class="sft-simple-title-icon material-icons" aria-hidden="true">{{
                    titleMaterialIconName
                }}</span>
                <span class="sft-simple-title">{{ titleText }}</span>
            </span>
        </template>
        <span class="sidebar-item-tooltip-wrap"><slot /></span>
    </a-tooltip>
</template>

<script>
export default {
    name: "SidebarItemTooltip",
    props: {
        titleText: {
            type: String,
            default: "",
        },
        richDescription: {
            type: String,
            default: "",
        },
        menuActive: {
            type: Boolean,
            default: true,
        },
        /** Sidebar satırındaki item.icon ile aynı yapı; material-icons ise başlıkta gösterilir */
        filterIcon: {
            type: Object,
            default: null,
        },
    },
    computed: {
        titleMaterialIconName() {
            var fi = this.filterIcon;
            if (!fi || fi.type !== "material-icons" || !fi.name) {
                return "";
            }
            return String(fi.name);
        },
        useRichTooltip() {
            return this.richDescription && String(this.richDescription).trim() !== "";
        },
        richDescriptionHtml() {
            return formatDescriptionToTooltipHtml(this.richDescription);
        },
        tooltipOverlayClass() {
            if (!this.menuActive) {
                return "hiddenToolTip";
            }
            if (this.useRichTooltip) {
                return "sidebar-filter-tooltip-card";
            }
            return "";
        },
    },
};

function escapeHtml(s) {
    return String(s)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
}

/** Paragraflar, "- " madde, "> " alıntı; satır içi **kalın** (küçük vurgu, font büyütmez) */
function applyInlineMarks(text) {
    var parts = String(text).split(/\*\*/);
    if (parts.length < 2) {
        return escapeHtml(text);
    }
    var html = "";
    for (var i = 0; i < parts.length; i++) {
        if (i % 2 === 0) {
            html += escapeHtml(parts[i]);
        } else if (parts[i].length) {
            html += '<strong class="sft-strong">' + escapeHtml(parts[i]) + "</strong>";
        }
    }
    return html;
}

function formatDescriptionToTooltipHtml(raw) {
    if (!raw || !String(raw).trim()) {
        return "";
    }
    var lines = String(raw).split(/\r?\n/);
    var out = [];
    var para = [];
    var listItems = [];
    var quoteLines = [];

    function flushPara() {
        if (para.length) {
            out.push('<p class="sft-p">' + applyInlineMarks(para.join(" ")) + "</p>");
            para = [];
        }
    }
    function flushList() {
        if (listItems.length) {
            var lis = listItems.map(function (li) {
                return '<li class="sft-li">' + applyInlineMarks(li) + "</li>";
            });
            out.push('<ul class="sft-ul">' + lis.join("") + "</ul>");
            listItems = [];
        }
    }
    function flushQuote() {
        if (quoteLines.length) {
            var qs = quoteLines.map(function (q) {
                return '<p class="sft-q">' + applyInlineMarks(q) + "</p>";
            });
            out.push('<blockquote class="sft-quote">' + qs.join("") + "</blockquote>");
            quoteLines = [];
        }
    }
    function flushAll() {
        flushQuote();
        flushList();
        flushPara();
    }

    for (var j = 0; j < lines.length; j++) {
        var line = lines[j];
        var t = line.trim();
        var bullet = line.match(/^\s*-\s+(.*)$/);
        var quote = line.match(/^\s*>\s*(.*)$/);
        if (bullet) {
            flushPara();
            flushQuote();
            listItems.push(bullet[1]);
        } else if (quote) {
            flushPara();
            flushList();
            quoteLines.push(quote[1]);
        } else if (t === "") {
            flushAll();
        } else {
            flushList();
            flushQuote();
            para.push(t);
        }
    }
    flushAll();
    return out.join("");
}
</script>

<style lang="scss">
/* Ant tooltip: açık kart görünümü (rich) */
.sidebar-filter-tooltip-card.ant-tooltip {
    max-width: 320px;
    padding-left: 2px;

    .ant-tooltip-inner {
        background: #ffffff;
        color: #1f2937;
        padding: 0;
        border-radius: 14px;
        box-shadow: 0 12px 32px rgba(15, 23, 42, 0.14), 0 0 0 1px rgba(15, 23, 42, 0.05);
        min-width: 232px;
        max-width: 320px;
    }

    .ant-tooltip-arrow::before {
        background: #ffffff;
        box-shadow: 0 0 0 1px rgba(15, 23, 42, 0.05);
    }
}

.sft-card {
    text-align: left;
    padding: 14px 16px 16px;

    .sft-card-title-row {
        display: flex;
        align-items: center;
        gap: 10px;
        margin-bottom: 10px;
        padding-bottom: 10px;
        border-bottom: 1px solid #e8ecf1;
    }

    .sft-card-title-icon {
        flex-shrink: 0;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-size: 20px;
        line-height: 1;
        color: #4f46e5;
    }

    .sft-card-title {
        flex: 1;
        min-width: 0;
        font-size: 13px;
        font-weight: 700;
        line-height: 1.35;
        color: #111827;
        letter-spacing: 0.01em;
    }

    .sft-card-body {
        font-size: 12px;
        line-height: 1.55;
        color: #4b5563;
        word-break: break-word;

        .sft-p {
            margin: 0 0 10px;
            &:last-child {
                margin-bottom: 0;
            }
        }

        .sft-ul {
            margin: 0 0 10px;
            padding-left: 1.1rem;
            list-style: disc;
        }

        .sft-li {
            margin: 5px 0;
            padding-left: 2px;
            &:first-child {
                margin-top: 0;
            }
            &:last-child {
                margin-bottom: 0;
            }
        }

        .sft-quote {
            margin: 8px 0 10px;
            padding: 9px 11px;
            border-left: 3px solid #a5b4fc;
            background: #f8fafc;
            border-radius: 0 8px 8px 0;

            .sft-q {
                margin: 0 0 6px;
                font-size: 12px;
                line-height: 1.5;
                color: #4b5563;
                &:last-child {
                    margin-bottom: 0;
                }
            }
        }

        .sft-strong {
            font-weight: 700;
            color: #111827;
        }
    }
}

.sft-simple-title-wrap {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    max-width: 100%;
    text-align: left;
}

.sft-simple-title-icon {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    line-height: 1;
    color: #4f46e5;
}

.sft-simple-title {
    display: inline-block;
    line-height: 1.35;
}

.sidebar-item-tooltip-wrap {
    display: block;
    width: 100%;
}
</style>
